import { useRef } from "react";
import { createPortal } from "react-dom";
import { Camera, Images, FilePdf } from "@phosphor-icons/react";
import { capturePhoto, pickPhotos } from "../lib/native";

/**
 * Menu nativo padrão para inserir/selecionar foto (iOS/Android):
 * "Tirar foto" → abre a câmera direto; "Escolher da galeria" → galeria (múltipla se `multiple`).
 * Quando `allowFile` é true, mostra também "Escolher arquivo (PDF)" — usado em KYC e orçamentos.
 * Uso: no nativo, abra este sheet; no web, use o <input type=file>.
 * onPick recebe SEMPRE um array de File.
 */
export function PhotoSourceSheet({
  open, onClose, onPick, multiple = false, limit = 10,
  allowFile = false, fileAccept = "image/*,application/pdf",
}) {
  const fileRef = useRef(null);
  if (!open) return null;

  const take = async () => {
    onClose?.();
    try { const f = await capturePhoto("camera"); if (f) onPick?.([f]); } catch { /* cancelado */ }
  };
  const gallery = async () => {
    onClose?.();
    try {
      if (multiple) { const files = await pickPhotos(limit); if (files?.length) onPick?.(files); }
      else { const f = await capturePhoto("photos"); if (f) onPick?.([f]); }
    } catch { /* cancelado */ }
  };
  // Arquivo/PDF: mantém o sheet aberto até o retorno do seletor do SO (não desmontar o input).
  const chooseFile = () => { fileRef.current?.click(); };
  const onFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    onClose?.();
    if (files.length) onPick?.(files);
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="photo-source-sheet">
      <div
        className="bg-white w-full rounded-t-2xl p-4 space-y-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-bold text-slate-700 text-center mb-1">{allowFile ? "Adicionar arquivo" : "Adicionar foto"}</div>
        <button type="button" onClick={take} className="w-full py-3 rounded-xl border-2 border-[#004E98] text-[#004E98] font-bold flex items-center justify-center gap-2" data-testid="photo-source-camera">
          <Camera size={18} weight="bold" /> Tirar foto
        </button>
        <button type="button" onClick={gallery} className="w-full py-3 rounded-xl bg-[#004E98] text-white font-bold flex items-center justify-center gap-2" data-testid="photo-source-gallery">
          <Images size={18} weight="bold" /> Escolher da galeria
        </button>
        {allowFile && (
          <>
            <button type="button" onClick={chooseFile} className="w-full py-3 rounded-xl border-2 border-slate-300 text-slate-700 font-bold flex items-center justify-center gap-2" data-testid="photo-source-file">
              <FilePdf size={18} weight="bold" /> Escolher arquivo (PDF)
            </button>
            <input ref={fileRef} type="file" accept={fileAccept} onChange={onFileInput} style={{ display: "none" }} data-testid="photo-source-file-input" />
          </>
        )}
        <button type="button" onClick={onClose} className="w-full text-slate-500 text-sm py-2" data-testid="photo-source-cancel">Cancelar</button>
      </div>
    </div>,
    document.body
  );
}
